import { useState, useMemo } from 'react'
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  addMonths,
  subMonths,
  isToday,
} from 'date-fns'
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  CheckCircle,
  PlayCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface Task {
  id: string
  title: string
  description?: string
  type: 'evidence-due' | 'document-review' | 'risk-assessment' | 'vendor-assessment' | 'custom'
  assigned_to?: string
  assigned_to_username?: string
  assigned_to_email?: string
  created_by: string
  created_by_username?: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  status: 'open' | 'in-progress' | 'pending-approval' | 'approved' | 'rejected' | 'completed' | 'cancelled'
  due_date?: number
  related_control_id?: string
  control_number?: string
  control_name?: string
  related_document_id?: string
  document_title?: string
  related_risk_id?: string
  risk_title?: string
  related_vendor_id?: string
  vendor_name?: string
  evidence_count?: number
  created_at: number
  updated_at: number
  completed_at?: number
}

interface TaskCalendarProps {
  tasks: Task[]
  onTaskClick: (task: Task) => void
  onDateClick?: (date: Date) => void
}

// ============================================================================
// CONSTANTS
// ============================================================================

const priorityColors: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  critical: { bg: 'bg-red-50', border: 'border-l-red-500', text: 'text-red-900', dot: 'bg-red-500' },
  high: { bg: 'bg-orange-50', border: 'border-l-orange-500', text: 'text-orange-900', dot: 'bg-orange-500' },
  medium: { bg: 'bg-blue-50', border: 'border-l-blue-500', text: 'text-blue-900', dot: 'bg-blue-500' },
  low: { bg: 'bg-gray-50', border: 'border-l-gray-400', text: 'text-gray-900', dot: 'bg-gray-400' },
}

const statusIcons: Record<string, { icon: any; color: string }> = {
  open: { icon: Clock, color: 'text-blue-500' },
  'in-progress': { icon: PlayCircle, color: 'text-amber-500' },
  completed: { icon: CheckCircle, color: 'text-green-500' },
  cancelled: { icon: XCircle, color: 'text-gray-400' },
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function TaskCalendar({ tasks, onTaskClick, onDateClick }: TaskCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date())

  // Get all days to display in the calendar grid
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }) // Sunday
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }, [currentMonth])

  // Group tasks by date
  const tasksByDate = useMemo(() => {
    const grouped: Record<string, Task[]> = {}

    tasks.forEach((task) => {
      if (task.due_date) {
        const dateKey = format(new Date(task.due_date), 'yyyy-MM-dd')
        if (!grouped[dateKey]) {
          grouped[dateKey] = []
        }
        grouped[dateKey].push(task)
      }
    })

    // Sort tasks within each day by priority (critical first) then by status
    const priorityOrder = ['critical', 'high', 'medium', 'low']
    const statusOrder = ['open', 'in-progress', 'completed', 'cancelled']

    Object.keys(grouped).forEach((dateKey) => {
      grouped[dateKey].sort((a, b) => {
        const priorityDiff = priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority)
        if (priorityDiff !== 0) return priorityDiff
        return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status)
      })
    })

    return grouped
  }, [tasks])

  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1))
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1))
  const goToToday = () => setCurrentMonth(new Date())

  const isOverdue = (task: Task) => {
    if (!task.due_date) return false
    if (task.status === 'completed' || task.status === 'cancelled') return false
    return task.due_date < Date.now()
  }

  // Day names header
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div className="card overflow-hidden">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Today
          </button>
          <div className="flex items-center border rounded-lg overflow-hidden">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 px-4 py-2 bg-gray-50 border-b text-xs">
        <span className="text-gray-500 font-medium">Priority:</span>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-gray-600">Critical</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-orange-500" />
          <span className="text-gray-600">High</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-gray-600">Medium</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-gray-400" />
          <span className="text-gray-600">Low</span>
        </div>
      </div>

      {/* Day Names Header */}
      <div className="grid grid-cols-7 bg-gray-50 border-b">
        {dayNames.map((day) => (
          <div
            key={day}
            className="py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, index) => {
          const dateKey = format(day, 'yyyy-MM-dd')
          const dayTasks = tasksByDate[dateKey] || []
          const isCurrentMonth = isSameMonth(day, currentMonth)
          const isTodayDate = isToday(day)
          const maxVisibleTasks = 3

          return (
            <div
              key={day.toISOString()}
              className={`min-h-[120px] border-b border-r p-1 ${
                !isCurrentMonth ? 'bg-gray-50' : 'bg-white'
              } ${index % 7 === 0 ? 'border-l' : ''}`}
              onClick={() => onDateClick?.(day)}
            >
              {/* Day Number */}
              <div className="flex justify-between items-start mb-1">
                <span
                  className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium ${
                    isTodayDate
                      ? 'bg-primary-600 text-white'
                      : isCurrentMonth
                      ? 'text-gray-900'
                      : 'text-gray-400'
                  }`}
                >
                  {format(day, 'd')}
                </span>
                {dayTasks.length > maxVisibleTasks && (
                  <span className="text-xs text-gray-500 px-1">
                    +{dayTasks.length - maxVisibleTasks} more
                  </span>
                )}
              </div>

              {/* Tasks */}
              <div className="space-y-1">
                {dayTasks.slice(0, maxVisibleTasks).map((task) => {
                  const colors = priorityColors[task.priority] || priorityColors.medium
                  const status = statusIcons[task.status] || statusIcons.open
                  const StatusIcon = status.icon
                  const taskIsOverdue = isOverdue(task)

                  return (
                    <button
                      key={task.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        onTaskClick(task)
                      }}
                      className={`w-full text-left px-1.5 py-1 rounded text-xs border-l-2 ${colors.bg} ${colors.border} ${colors.text} hover:opacity-80 transition-opacity truncate ${
                        task.status === 'completed' ? 'line-through opacity-60' : ''
                      } ${taskIsOverdue ? 'ring-1 ring-red-300' : ''}`}
                      title={`${task.title}${task.assigned_to_username ? ` (${task.assigned_to_username})` : ''}`}
                    >
                      <div className="flex items-center gap-1">
                        <StatusIcon className={`h-3 w-3 flex-shrink-0 ${status.color}`} />
                        <span className="truncate">{task.title}</span>
                        {taskIsOverdue && (
                          <AlertCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between text-sm text-gray-600">
        <div>
          <span className="font-medium">{tasks.filter((t) => t.due_date).length}</span> tasks with due dates
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-blue-500" />
            <span>{tasks.filter((t) => t.status === 'open').length} open</span>
          </div>
          <div className="flex items-center gap-1">
            <PlayCircle className="h-4 w-4 text-amber-500" />
            <span>{tasks.filter((t) => t.status === 'in-progress').length} in progress</span>
          </div>
          <div className="flex items-center gap-1">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span>
              {tasks.filter((t) => {
                if (!t.due_date) return false
                if (t.status === 'completed' || t.status === 'cancelled') return false
                return t.due_date < Date.now()
              }).length}{' '}
              overdue
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
